import React, { useRef } from 'react'
import { Typography, Button } from 'antd'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { EnergyMode } from '@vacationtracker/shared/types/user-insight'
import EnergyLevelImage from './energy-level-image'
import FeedbackModal, { FeedbackModalRef } from '../feedback-modal'
import { EnergyModeConfig } from '../../types'

const { Text } = Typography

interface EnergyGaugeProps {
  score: number
  mode: string
  description: string
  currentEnergyModeConfig: EnergyModeConfig
  energyModeNameMap: Record<EnergyMode, string>
  onTooltipView?: (tooltipType: 'energy_score' | 'trend_point' | 'factor_breakdown' | 'pto_suggestion' | 'department_comparison' | 'energy_mode',
    tooltipContent: string, elementHovered: string, section: 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown', hoverDurationMs: number) => void
  onActionTaken?: (actionType: 'button_click' | 'chart_interaction' | 'link_click' | 'gauge_click', actionName: string,
    elementType: 'button' | 'link' | 'chart' | 'gauge' | 'card' | 'suggestion', section: 'header' | 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown',
    ledToNavigation?: boolean, destinationUrl?: string) => void
  onFeedbackLinkClick?: (location: 'header' | 'footer' | 'energy_gauge' | 'suggestions' | 'score_breakdown' | 'error_state') => void
  onFeedbackSubmit?: (feedbackReason: string, userSuggestedScore: number | undefined, feedbackText: string, timeToSubmitMs: number,
    submissionMethod?: 'quick_rating' | 'detailed_form' | 'inline_feedback', allowContact?: boolean, reportedEnergyMode?: EnergyMode) => void
  onFeedbackSuccess?: () => void
}

const EnergyGauge: React.FC<EnergyGaugeProps> = ({
  score, mode, currentEnergyModeConfig, energyModeNameMap,
  onFeedbackLinkClick, onFeedbackSubmit, onFeedbackSuccess,
}) => {
  const feedbackModalRef = useRef<FeedbackModalRef>(null)

  const energyMode = mode

  const handleScoreFeedback = () => {
    feedbackModalRef.current?.openModal()
  }

  return (
    <div className={`energy-gauge energy-gauge--${energyMode}`}>
      <div className="energy-gauge-header">
        <div className="energy-gauge-description">
          <Text type="secondary">
            <IntlMessages id="energyLevel.yourCurrentEnergyLevel" />
          </Text>
        </div>
        <Typography.Title level={3} className="energy-gauge-title">
          {mode}
        </Typography.Title>
      </div>

      <div className="energy-gauge-circle">
        <EnergyLevelImage score={score} />
      </div>

      <div className="energy-gauge-feedback">
        <div className="energy-gauge-feedback-text-container">
          <div className="energy-gauge-feedback-text">
            <Text>
              <IntlMessages id="energyLevel.isEnergyLevelInaccurate" />
            </Text>
          </div>
          <div className="energy-gauge-feedback-subtext">
            <Text>
              <IntlMessages id="energyLevel.helpUsImprove" />
            </Text>
          </div>
        </div>
        <Button className="energy-gauge-feedback-button" onClick={handleScoreFeedback}>
          <IntlMessages id="energyLevel.adjustYourEnergy" />
        </Button>
      </div>

      <FeedbackModal
        ref={feedbackModalRef}
        currentScore={score}
        currentEnergyModeConfig={currentEnergyModeConfig}
        energyModeNameMap={energyModeNameMap}
        onFeedbackLinkClick={onFeedbackLinkClick}
        onFeedbackSubmit={onFeedbackSubmit}
        onFeedbackSuccess={onFeedbackSuccess}
      />
    </div>
  )
}

export default EnergyGauge
